import { Helmet } from '@dr.pogodin/react-helmet';
import { type ReactElement } from 'react';
import { ScrollRestoration } from "react-router";
import Footer from '@/layouts/parts/Footer';
import Header from '@/layouts/parts/Header';
import HomepageSameAsJsonLd from '@/components/HomepageSameAsJsonLd';
import WebPageFallbackJsonLd from '@/components/WebPageFallbackJsonLd';
import Website from '@/layouts/Website';
import GoogleAnalytics from '@/components/GoogleAnalytics';
import TrustedTVTag from '@/components/TrustedTVTag';
import FloatingQuoteCTA from '@/components/FloatingQuoteCTA';

/**
 * Root layout component that wraps all pages with consistent header and footer.
 *
 * To customize the header or footer, directly edit the Header.tsx and Footer.tsx
 * files in the layouts/parts directory.
 *
 * Site-wide <title> and <meta> live in the <Helmet> below. Individual pages can
 * override them by rendering their own <Helmet> — last-mounted wins.
 */
interface RootLayoutProps {
  children: ReactElement;
}
export default function RootLayout({
  children
}: RootLayoutProps) {
  return <Website>
      <GoogleAnalytics />
      <TrustedTVTag />
      <Helmet>
        <title>Sand and Sun Travels | Luxury Travel Planning</title>
        <meta name="description" content="Sand and Sun Travels — bilingual luxury travel advisors crafting custom honeymoons, family vacations, and cruises to the world's most breathtaking destinations." />
        <meta name="robots" content="index, follow" />
        {/* Core OG fallbacks — individual pages override these with their own <Helmet> */}
        <meta property="og:title" content="Sand and Sun Travels | Luxury Travel Planning" />
        <meta property="og:description" content="Sand and Sun Travels — bilingual luxury travel advisors crafting custom honeymoons, family vacations, and cruises to the world's most breathtaking destinations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sandandsuntravels.com/" />
        <meta property="og:image" content="https://www.sandandsuntravels.com/airo-assets/images/pages/home/og-image" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Sand and Sun Travels" />
        <meta property="og:locale" content="en_US" />
        <meta property="fb:app_id" content="1594413212271380" />
        <meta name="p:domain_verify" content="1810249f47893351a9ea9594bc18649c" />
        {/* Site-wide Twitter/X Card defaults — individual pages override these */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@sandandsunt" />
        <meta name="twitter:title" content="Sand and Sun Travels | Luxury Travel Planning" />
        <meta name="twitter:description" content="Sand and Sun Travels — bilingual luxury travel advisors crafting custom honeymoons, family vacations, and cruises to the world's most breathtaking destinations." />
        <meta name="twitter:image" content="https://www.sandandsuntravels.com/airo-assets/images/pages/home/og-image" />
        <link rel="alternate" type="application/rss+xml" title="Sand and Sun Travels Blog Feed" href="/feed.xml" />
      </Helmet>
      <HomepageSameAsJsonLd />
      <WebPageFallbackJsonLd />
      <ScrollRestoration />
      <Header />
      {children}
      <Footer />
      <FloatingQuoteCTA />
    </Website>;
}
